import dynamic from 'next/dynamic';
import { GetStaticPropsContext } from 'next/types';

import { PageCategory } from '~/features/analytics/constants';
import { getCatalogItemsFromServer } from '~/features/catalog/controllers/get-catalog-items-from-server';
import { CatalogItem } from '~/features/catalog/models/catalog-item';
import { getCompany } from '~/features/companies/controllers/get-company';
import { CompanyContent } from '~/features/companies/models/company';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { logger } from '~/features/logging/logger';
import { FeaturedProgramSection } from '~/features/microsoft-ai-skills-fest/components/featured-program-section';
import { MicrosoftAiSkillsFestHeroSection } from '~/features/microsoft-ai-skills-fest/components/hero-section';
import { ProgramList } from '~/features/microsoft-ai-skills-fest/components/program-list';
import { StartForFreeSection } from '~/features/microsoft-ai-skills-fest/components/start-for-free-section';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';

const CopilotProgramsSection = dynamic(
  () =>
    import('~/features/microsoft-ai-skills-fest/components/copilot-programs-section').then(
      (mod) => mod.CopilotProgramsSection
    ),
  {
    ssr: false,
  }
);

export default function MicrosoftAiSkillsFestPage({
  page,
  microsoft,
  featuredFreeProgram,
  featuredPrograms,
  copilotPrograms,
}: {
  page: Page;
  microsoft: CompanyContent;
  featuredFreeProgram: CatalogItem;
  featuredPrograms: CatalogItem[];
  copilotPrograms: CatalogItem[];
}) {
  return (
    <>
      <PageHead page={page} pageCategory={PageCategory.Uncategorized} />
      <PageLayout withoutVerticalPadding>
        <MicrosoftAiSkillsFestHeroSection microsoftLogo={microsoft.hifiLogo} />
        <StartForFreeSection featuredFreeProgram={featuredFreeProgram} />
        {copilotPrograms.length > 0 && (
          <CopilotProgramsSection programsSlot={<ProgramList programs={copilotPrograms} />} />
        )}
        <FeaturedProgramSection programsSlot={<ProgramList programs={featuredPrograms} />} />
      </PageLayout>
    </>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  // Stop additional locale pages except the default locale from being built
  if (locale !== DEFAULT_LANGUAGE) {
    return {
      notFound: true,
    };
  }

  if (!locale) {
    logger.fatal('Legal locale not found', {
      file: 'src/pages/legal/[slug].tsx',
      function: 'getStaticProps',
      locale,
    });

    throw new Error('Locale not found');
  }

  const [page, microsoft] = await Promise.all([getPage('/microsoft-ai-skills-fest'), getCompany('Microsoft')]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  const featuredFreeProgram = getCatalogItemsFromServer(['cd14249'])?.[0] || null;
  const featuredPrograms = getCatalogItemsFromServer(['nd444', 'nd073', 'nd0277']);

  const copilotPrograms = getCatalogItemsFromServer(['cd14097', 'cd14096']);

  return {
    props: {
      page,
      microsoft,
      featuredFreeProgram,
      featuredPrograms,
      copilotPrograms,
    },
  };
}
